<template>
  <div class="monetary-dashboard" :class="{ 'monetary-dashboard--dark': isDarkSkin }">

    <div class="monetary-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <calendar-bar v-model="dateRange" @input="scheduleRefresh"/>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <div class="kpi-grid">
        <div class="kpi-cell">
          <!-- Same filters as the Deposits table below — the modal, the table
               and the card always agree. -->
          <div class="kpi-card kpi-card--success kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
               @click="openCardTx($t('Total Deposits'), depositsFilters)">
            <div class="kpi-card__icon">
              <i class="fa-solid fa-coins" style="font-size: 22px;"></i>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Deposits') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalDeposits) }}</div>
              <div v-if="delta.totalDeposits" class="kpi-card__delta"
                   :class="{ 'is-up': delta.totalDeposits.positive, 'is-down': !delta.totalDeposits.positive }">
                <feather-icon :icon="delta.totalDeposits.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.totalDeposits.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.depositsCount }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--danger kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
               @click="openCardTx($t('Total Withdrawals'), withdrawalsFilters)">
            <div class="kpi-card__icon">
              <feather-icon icon="ArrowDownCircleIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Total Withdrawals') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.totalWithdrawals) }}</div>
              <div v-if="delta.totalWithdrawals" class="kpi-card__delta"
                   :class="{ 'is-up': delta.totalWithdrawals.positive, 'is-down': !delta.totalWithdrawals.positive }">
                <feather-icon :icon="delta.totalWithdrawals.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.totalWithdrawals.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.withdrawalsCount }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <!-- NOT clickable: net = deposits + withdrawals, two transaction
               types at once — the MonetariesTable filter takes a single type,
               so no filter reproduces this number. -->
          <div class="kpi-card kpi-card--primary">
            <div class="kpi-card__icon">
              <feather-icon icon="TrendingUpIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('Net Deposits') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.netDeposits) }}</div>
              <div v-if="delta.netDeposits" class="kpi-card__delta"
                   :class="{ 'is-up': delta.netDeposits.positive, 'is-down': !delta.netDeposits.positive }">
                <feather-icon :icon="delta.netDeposits.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.netDeposits.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="PercentIcon" size="12" class="mr-25"/>
                {{ $t('Withdrawal Rate') }}: {{ withdrawalRate }}%
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--primary kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
               @click="openCardTx($t('FTD'), { ...depositsFilters, isFtd: true })">
            <div class="kpi-card__icon">
              <feather-icon icon="UserPlusIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('FTD') }}</div>
              <div class="kpi-card__value">{{ statistics.ftdCount }}</div>
              <div v-if="delta.ftdCount" class="kpi-card__delta"
                   :class="{ 'is-up': delta.ftdCount.positive, 'is-down': !delta.ftdCount.positive }">
                <feather-icon :icon="delta.ftdCount.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.ftdCount.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <i class="fa-solid fa-coins mr-25" style="font-size: 11px;"></i>
                {{ convertToEURO(statistics.ftdAmount) }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <div class="kpi-card kpi-card--warning kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
               @click="openCardTx($t('STD'), { ...depositsFilters, isStd: true })">
            <div class="kpi-card__icon">
              <feather-icon icon="RefreshCwIcon" size="24"/>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('STD') }}</div>
              <div class="kpi-card__value">{{ statistics.stdCount }}</div>
              <div v-if="delta.stdCount" class="kpi-card__delta"
                   :class="{ 'is-up': delta.stdCount.positive, 'is-down': !delta.stdCount.positive }">
                <feather-icon :icon="delta.stdCount.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.stdCount.label }} {{ $t('vs prev') }}
              </div>
              <div class="kpi-card__footer">
                <i class="fa-solid fa-coins mr-25" style="font-size: 11px;"></i>
                {{ convertToEURO(statistics.stdAmount) }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <div class="kpi-cell">
          <!-- Pending approvals: ONE card, diagonally split into two
               independently clickable halves — deposits / withdrawals -->
          <div class="kpi-card kpi-card--pending kpi-card--split">
            <button type="button" class="kpi-half kpi-half--dep" v-b-tooltip.hover
                    :title="$t('finance.clickToReview')" @click="openPending('DEPOSIT')">
              <span class="kpi-half__inner">
                <span class="kpi-half__title"><i class="fa-solid fa-arrow-down mr-25"></i>{{ $t('monetaryDashboard.pendingDeposits') }}</span>
                <span class="kpi-half__value">{{ statistics.pendingDepositsCount }}</span>
              </span>
            </button>
            <button type="button" class="kpi-half kpi-half--wd" v-b-tooltip.hover
                    :title="$t('finance.clickToReview')" @click="openPending('WITHDRAWAL')">
              <span class="kpi-half__inner">
                <span class="kpi-half__value">{{ statistics.pendingWithdrawalsCount }}</span>
                <span class="kpi-half__title">{{ $t('monetaryDashboard.pendingWithdrawals') }}<i class="fa-solid fa-arrow-up ml-25"></i></span>
              </span>
            </button>
            <div class="kpi-card__glow"></div>
          </div>
        </div>

        <!-- TXF (taxes): always shown. Its money folds INTO Total Deposits /
             Withdrawals / Net unless the "Exclude TXF" filter is ticked. -->
        <div class="kpi-cell">
          <div class="kpi-card kpi-card--txf kpi-card--clickable" role="button" tabindex="0"
               v-b-tooltip.hover :title="$t('monetaryDashboard.txfHint')"
               @click="openCardTx($t('monetaryDashboard.txf'), txfCardFilters)">
            <div class="kpi-card__icon">
              <i class="fa-solid fa-file-invoice-dollar" style="font-size: 22px;"></i>
            </div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('monetaryDashboard.txf') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(statistics.txfAmount) }}</div>
              <div v-if="delta.txfAmount" class="kpi-card__delta"
                   :class="{ 'is-up': delta.txfAmount.positive, 'is-down': !delta.txfAmount.positive }">
                <feather-icon :icon="delta.txfAmount.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="12"/>
                {{ delta.txfAmount.label }}
              </div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>
                {{ $t('Count') }}: {{ statistics.txfCount }}
              </div>
            </div>
            <i class="fas fa-expand kpi-card__expand"></i>
            <div class="kpi-card__glow"></div>
          </div>
        </div>
      </div>

      <!-- FAKE money (approved transactions marked realType FAKE): a slim
           strip of mini-pills — deliberately much smaller than the real KPI
           cards so fake money reads as a footnote, never as real money. -->
      <div class="fake-strip">
        <span class="fake-strip__badge">FAKE</span>
        <div class="fake-pill fake-pill--click" role="button" tabindex="0"
             v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
             @click="openCardTx($t('Fake Deposits'), { ...depositsFilters, realType: ['FAKE'] })">
          <i class="fa-solid fa-coins" style="font-size: 12px;"></i>
          <span class="fake-pill__label">{{ $t('Fake Deposits') }}</span>
          <span class="fake-pill__value">{{ convertToEURO(statistics.fakeDeposits) }}</span>
          <span class="fake-pill__count">({{ statistics.fakeDepositsCount }})</span>
          <span v-if="delta.fakeDeposits" class="fake-pill__delta"
                :class="{ 'is-up': delta.fakeDeposits.positive, 'is-down': !delta.fakeDeposits.positive }">
            <feather-icon :icon="delta.fakeDeposits.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="10"/>
            {{ delta.fakeDeposits.label }}
          </span>
        </div>
        <div class="fake-pill fake-pill--click" role="button" tabindex="0"
             v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
             @click="openCardTx($t('Fake Withdrawals'), { ...withdrawalsFilters, realType: ['FAKE'] })">
          <feather-icon icon="ArrowDownCircleIcon" size="13"/>
          <span class="fake-pill__label">{{ $t('Fake Withdrawals') }}</span>
          <span class="fake-pill__value">{{ convertToEURO(statistics.fakeWithdrawals) }}</span>
          <span class="fake-pill__count">({{ statistics.fakeWithdrawalsCount }})</span>
          <span v-if="delta.fakeWithdrawals" class="fake-pill__delta"
                :class="{ 'is-up': delta.fakeWithdrawals.positive, 'is-down': !delta.fakeWithdrawals.positive }">
            <feather-icon :icon="delta.fakeWithdrawals.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="10"/>
            {{ delta.fakeWithdrawals.label }}
          </span>
        </div>
        <!-- NOT clickable, same reason as the real Net card: net mixes
             deposits + withdrawals, which a single table filter can't show. -->
        <div class="fake-pill">
          <feather-icon icon="TrendingUpIcon" size="13"/>
          <span class="fake-pill__label">{{ $t('Fake Net Deposits') }}</span>
          <span class="fake-pill__value">{{ convertToEURO(statistics.fakeNetDeposits) }}</span>
          <span v-if="delta.fakeNetDeposits" class="fake-pill__delta"
                :class="{ 'is-up': delta.fakeNetDeposits.positive, 'is-down': !delta.fakeNetDeposits.positive }">
            <feather-icon :icon="delta.fakeNetDeposits.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="10"/>
            {{ delta.fakeNetDeposits.label }}
          </span>
        </div>
        <div class="fake-pill fake-pill--click" role="button" tabindex="0"
             v-b-tooltip.hover :title="$t('monetaryDashboard.viewTransactions')"
             @click="openCardTx($t('Fake FTDs'), { ...depositsFilters, realType: ['FAKE'], isFtd: true })">
          <feather-icon icon="UserPlusIcon" size="13"/>
          <span class="fake-pill__label">{{ $t('Fake FTDs') }}</span>
          <span class="fake-pill__value">{{ statistics.fakeFtdCount }}</span>
          <span class="fake-pill__count">({{ convertToEURO(statistics.fakeFtdAmount) }})</span>
          <span v-if="delta.fakeFtdCount" class="fake-pill__delta"
                :class="{ 'is-up': delta.fakeFtdCount.positive, 'is-down': !delta.fakeFtdCount.positive }">
            <feather-icon :icon="delta.fakeFtdCount.positive ? 'ArrowUpIcon' : 'ArrowDownIcon'" size="10"/>
            {{ delta.fakeFtdCount.label }}
          </span>
        </div>
      </div>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ $t('Deposits') }}</h5>
          <b-button size="sm" variant="outline-primary" v-b-tooltip.hover :title="$t('columns.self')"
                    @click="openColumnsModal">
            <i class="fa-solid fa-bars"></i>
          </b-button>
        </b-card-header>
        <b-card-body>
          <monetaries-table v-bind:filters="depositsFilters" :columns="columns" :has-selection="false" trans-type="deposits"/>
        </b-card-body>
      </b-card>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50 d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ $t('Withdrawals') }}</h5>
          <b-button size="sm" variant="outline-primary" v-b-tooltip.hover :title="$t('columns.self')"
                    @click="openColumnsModal">
            <i class="fa-solid fa-bars"></i>
          </b-button>
        </b-card-header>
        <b-card-body>
          <monetaries-table v-bind:filters="withdrawalsFilters" :columns="columns" :has-selection="false" trans-type="withdrawals"/>
        </b-card-body>
      </b-card>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50">
          <h5 class="mb-0">{{ $t('Deposits & Withdrawals over time') }}</h5>
        </b-card-header>
        <b-card-body>
          <monetary-time-series-chart :points="statistics.series" :loading="loading" :is-dark-skin="isDarkSkin"/>
        </b-card-body>
      </b-card>

    </b-overlay>

    <!-- Pending approvals: review + approve/decline -->
    <b-modal v-model="pendingModalVisible" size="xl" centered hide-footer
             :title="pendingModalTitle" modal-class="pending-modal" @hidden="onPendingHidden">
      <monetaries-table v-if="pendingModalVisible" :filters="pendingFilters" :columns="pendingColumns"
                        trans-type="pending" :has-selection="false" :lazy="false"
                        @input="pendingDirty = true"/>
    </b-modal>

    <!-- KPI card drill-down: raw transactions behind the clicked number -->
    <insight-transactions-modal ref="txModal"/>

    <!-- Per-table column show/hide/reorder (shared by the Deposits + Withdrawals tables) -->
    <columns-modal ref="columns-modal" v-model="columns" v-bind:columns-names="columnsNames"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import FilterCard from '@/components/widget/FilterCard';
import MonetariesTable from '@/components/tables/MonetariesTable';
import MonetaryTimeSeriesChart from '@/components/chart/MonetaryTimeSeriesChart';
import MonetaryDashboardSearchRequest from '@/model/Dashboard/MonetaryDashboardSearchRequest';
import MonetaryDashboardFilterFields from '@/model/filter/MonetaryDashboardFilterFields';
import InsightTransactionsModal from '@/components/modal/InsightTransactionsModal.vue';
import ColumnsModal from '@/components/modal/columnsModal';
import Monetaries from '@/model/monetary/Monetary';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'MonetaryDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],

  components: {
    CalendarBar,
    FilterCard,
    MonetariesTable,
    MonetaryTimeSeriesChart,
    InsightTransactionsModal,
    ColumnsModal,
  },

  data: () => ({
    filter: MonetaryDashboardSearchRequest(),
    filterFields: [],
    // Column show/hide/reorder prefs for the Deposits + Withdrawals tables
    // (persisted per-route in localStorage by columns-modal).
    columns: [],
    columnsNames: [
      ...Object.keys(Monetaries()),
      'clientDetails.accountId', 'clientDetails.clientId', 'clientDetails.email', 'clientDetails.country', 'clientDetails.affiliate',
      'paymentDetails.processorName', 'paymentDetails.comments',
    ],
    dateRange: { from: null, until: null },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    pendingModalVisible: false,
    // Which half of the split pending card was clicked (DEPOSIT / WITHDRAWAL)
    pendingType: null,
    // True once an approve/decline happened inside the pending modal —
    // only then is a dashboard re-pull on close worth it.
    pendingDirty: false,
    statistics: {
      totalDeposits: 0,
      depositsCount: 0,
      totalWithdrawals: 0,
      withdrawalsCount: 0,
      netDeposits: 0,
      ftdCount: 0,
      ftdAmount: 0,
      stdCount: 0,
      stdAmount: 0,
      pendingApprovalsCount: 0,
      pendingDepositsCount: 0,
      pendingWithdrawalsCount: 0,
      // FAKE money (approved transactions marked realType FAKE)
      fakeDeposits: 0,
      fakeDepositsCount: 0,
      fakeWithdrawals: 0,
      fakeWithdrawalsCount: 0,
      fakeNetDeposits: 0,
      fakeFtdCount: 0,
      fakeFtdAmount: 0,
      // TXF (taxes) — own KPI, never inside the deposit totals
      txfAmount: 0,
      txfCount: 0,
      series: [],
      previous: null,
    },
  }),
  watch: {
    filter: {
      handler: 'scheduleRefresh',
      deep: true,
    },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    viewKey() {
      return this.$route.name || 'monetary-dashboard';
    },
    withdrawalRate() {
      const deposits = this.statistics.totalDeposits || 0;
      const withdrawals = this.statistics.totalWithdrawals || 0;
      if (!deposits) return '0.00';
      return (withdrawals / deposits * -100).toFixed(2);
    },
    pendingFilters() {
      return {
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat(),
        },
        realType: this.filter.realOnly === false ? null : ['REAL'],
        // Both pending flavors: website REQUESTs + CRM-created PENDING rows.
        status: ['REQUEST', 'PENDING'],
        type: this.pendingType || null,
      };
    },
    pendingModalTitle() {
      const base = this.$t('Pending Approvals');
      if (this.pendingType === 'DEPOSIT') return `${base} · ${this.$t('monetaryDashboard.pendingDeposits')}`;
      if (this.pendingType === 'WITHDRAWAL') return `${base} · ${this.$t('monetaryDashboard.pendingWithdrawals')}`;
      return base;
    },
    pendingColumns() {
      return [
        { key: 'clientDetails.accountId', enable: true },
        { key: 'clientDetails.email', enable: true },
        { key: 'amount', enable: true },
        { key: 'type', enable: true },
        { key: 'paymentDetails.processorName', enable: true },
        { key: 'registrationTime', enable: true },
        { key: 'status', enable: true },
        { key: 'actions', enable: true },
      ];
    },
    depositsFilters() {
      return {
        type: 'DEPOSIT',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat()
        },
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED'],
        // TXF folds into the deposit KPIs unless excluded — the table below
        // must show the same rows or card and table disagree.
        action: this.filter.excludeTxf ? ['CREDIT_LINE'] : ['CREDIT_LINE', 'TAXES'],
        // Match the KPI aggregation, which always excludes test transactions
        // (isTest=false). Without this the table counts test deposits and
        // disagrees with the card count above.
        isTest: false,
      };
    },
    // TXF card drill-down: explicit action TAXES (which the backend lets
    // through even though the table hides TXF rows by default).
    txfCardFilters() {
      return {
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat()
        },
        status: ['APPROVED'],
        action: ['TAXES'],
        isTest: false,
      };
    },
    withdrawalsFilters() {
      return {
        type: 'WITHDRAWAL',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        userIds: this.filter.userIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat()
        },
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED'],
        // Same TXF fold-in rule as the deposits table.
        action: this.filter.excludeTxf ? ['CREDIT_LINE'] : ['CREDIT_LINE', 'TAXES'],
        // Match the KPI aggregation, which always excludes test transactions.
        isTest: false,
      };
    },
    delta() {
      const prev = this.statistics.previous;
      if (!prev) return {};
      return {
        totalDeposits: this.computeDelta(this.statistics.totalDeposits, prev.totalDeposits),
        totalWithdrawals: this.computeDelta(this.statistics.totalWithdrawals, prev.totalWithdrawals),
        netDeposits: this.computeDelta(this.statistics.netDeposits, prev.netDeposits),
        ftdCount: this.computeDelta(this.statistics.ftdCount, prev.ftdCount),
        stdCount: this.computeDelta(this.statistics.stdCount, prev.stdCount),
        fakeDeposits: this.computeDelta(this.statistics.fakeDeposits, prev.fakeDeposits),
        fakeWithdrawals: this.computeDelta(this.statistics.fakeWithdrawals, prev.fakeWithdrawals),
        fakeNetDeposits: this.computeDelta(this.statistics.fakeNetDeposits, prev.fakeNetDeposits),
        fakeFtdCount: this.computeDelta(this.statistics.fakeFtdCount, prev.fakeFtdCount),
        txfAmount: this.computeDelta(this.statistics.txfAmount, prev.txfAmount),
      };
    },
  },
  created() {
    this.filterFields = MonetaryDashboardFilterFields();
    this.dateRange= {
      from: this.$moment().startOf('week').format('YYYY-MM-DD'),
      until: this.$moment().endOf('week').format('YYYY-MM-DD'),
    }
  },
  mounted() {
    this.$root.$on('dashboard::refresh', this.scheduleRefresh);
  },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
  methods: {
    ...mapActions('dashboard', ['getMonetaryDashboard']),
    openColumnsModal() {
      this.$refs['columns-modal'].showModal();
    },
    openPending(type) {
      this.pendingType = type || null;
      this.pendingDirty = false;
      this.pendingModalVisible = true;
    },
    // KPI card → read-only list of the exact transactions the card counts.
    // (Filters mirror what the backend aggregation applies for that card.)
    openCardTx(title, filters) {
      this.$refs.txModal.show(title, filters);
    },
    onPendingHidden() {
      // Re-pull the dashboard ONLY when something was approved/declined
      // inside the modal — just looking and closing changes nothing.
      if (this.pendingDirty) {
        this.pendingDirty = false;
        this.scheduleRefresh();
      }
    },
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.dateRange.from) return;
      if (this.inflight) {
        this.pendingFetch = true;
        return;
      }
      this.doFetch();
    },
    doFetch() {
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
      this.inflight = true;
      this.loading = true;
      this.getMonetaryDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getMonetaryDashboard', 'dashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) {
            this.pendingFetch = false;
            this.doFetch();
          }
        });
    },
    setData(data) {
      if (!data) return;
      this.statistics.totalDeposits = data.totalDeposits || 0;
      this.statistics.depositsCount = data.depositsCount || 0;
      this.statistics.totalWithdrawals = data.totalWithdrawals || 0;
      this.statistics.withdrawalsCount = data.withdrawalsCount || 0;
      this.statistics.netDeposits = data.netDeposits || 0;
      this.statistics.ftdCount = data.ftdCount || 0;
      this.statistics.ftdAmount = data.ftdAmount || 0;
      this.statistics.stdCount = data.stdCount || 0;
      this.statistics.stdAmount = data.stdAmount || 0;
      this.statistics.pendingApprovalsCount = data.pendingApprovalsCount || 0;
      this.statistics.pendingDepositsCount = data.pendingDepositsCount || 0;
      this.statistics.pendingWithdrawalsCount = data.pendingWithdrawalsCount || 0;
      // FAKE fields: fall back to 0 — older cached responses may not have them.
      this.statistics.fakeDeposits = data.fakeDeposits || 0;
      this.statistics.fakeDepositsCount = data.fakeDepositsCount || 0;
      this.statistics.fakeWithdrawals = data.fakeWithdrawals || 0;
      this.statistics.fakeWithdrawalsCount = data.fakeWithdrawalsCount || 0;
      this.statistics.fakeNetDeposits = data.fakeNetDeposits || 0;
      this.statistics.fakeFtdCount = data.fakeFtdCount || 0;
      this.statistics.fakeFtdAmount = data.fakeFtdAmount || 0;
      this.statistics.txfAmount = data.txfAmount || 0;
      this.statistics.txfCount = data.txfCount || 0;
      this.statistics.series = data.series || [];
      this.statistics.previous = data.previous || null;
    },
    computeDelta(current, previous) {
      const cur = Number(current) || 0;
      const prev = Number(previous) || 0;
      if (prev === 0) {
        if (cur === 0) return null;
        return { label: '—', positive: cur >= 0 };
      }
      const pct = ((cur - prev) / Math.abs(prev)) * 100;
      const positive = pct >= 0;
      const sign = positive ? '+' : '';
      return { label: `${sign}${pct.toFixed(1)}%`, positive };
    },
  },
};
</script>

<style scoped lang="scss">
.monetary-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.kpi-grid {
  display: grid;
  /* Container-driven: columns wrap to the ACTUAL available width (whether the
     sidebar is open or not) instead of Bootstrap's viewport-based breakpoints,
     so a card is never squeezed narrow enough to truncate its amount. */
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 0.75rem;
  /* bottom margin restores the spacing the old b-col `mb-2` gave before the
     FAKE strip below. */
  margin: 0.5rem 0 1rem;
}

.kpi-cell {
  display: flex;
}

.kpi-card {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28),
              0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35),
                0 4px 8px rgba(20, 24, 50, 0.08);
  }

  &__icon {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.22);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
  }

  &__body {
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.82rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.86;
    margin-bottom: 0.25rem;
    /* Wrap instead of truncating — a narrow card shows "Total Deposits" on two
       lines rather than "Total…". */
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &__value {
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    /* Never clip the amount — let it wrap if the card is narrow. */
    white-space: normal;
    overflow-wrap: anywhere;
  }

  &__footer {
    margin-top: 0.45rem;
    font-size: 0.76rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
  }

  &__delta {
    margin-top: 0.35rem;
    font-size: 0.72rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);

    &.is-up { color: #d6ffe8; }
    &.is-down { color: #ffe0e0; }
  }

  &__glow {
    position: absolute;
    top: -40%;
    right: -20%;
    width: 220px;
    height: 220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none;
    z-index: 1;
  }

  &--primary {
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }

  &--success {
    background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%);
  }

  &--warning {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%);
  }

  &--danger {
    background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%);
  }

  &--pending {
    background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 55%, #5b21b6 100%);
  }

  /* Pending card: same shape, diagonally split into two clickable halves */
  &--split {
    padding: 0;
    min-height: 116px;

    .kpi-half {
      position: absolute;
      inset: 0;
      border: none;
      background: transparent;
      color: #fff;
      cursor: pointer;
      z-index: 2;
      padding: 0;
      /* The card itself lifts like every other KPI card — the halves only
         highlight, smoothly, so hovering never looks glitchy. */
      transition: background 0.18s ease;

      &:focus { outline: none; }

      /* top-left triangle (deposits) — diagonal runs top-right → bottom-left */
      &--dep {
        clip-path: polygon(0 0, 100% 0, 0 100%);
        background: rgba(255, 255, 255, 0.10);
        &:hover { background: rgba(255, 255, 255, 0.4); }
      }
      /* bottom-right triangle (withdrawals) */
      &--wd {
        clip-path: polygon(100% 0, 100% 100%, 0 100%);
        background: rgba(0, 0, 0, 0.16);
        &:hover { background: rgba(0, 0, 0, 0.45); }
      }

      &:hover .kpi-half__inner { transform: scale(1.08); }
    }

    .kpi-half__inner {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      transition: transform 0.18s ease;
    }
    .kpi-half--dep .kpi-half__inner {
      top: 12px;
      left: 16px;
      align-items: flex-start;
      text-align: left;
      transform-origin: top left;
    }
    .kpi-half--wd .kpi-half__inner {
      bottom: 12px;
      right: 16px;
      align-items: flex-end;
      text-align: right;
      transform-origin: bottom right;
    }
    .kpi-half__title {
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      opacity: 0.92;
      display: inline-flex;
      align-items: center;
    }
    .kpi-half__value {
      font-size: 1.4rem;
      font-weight: 800;
      line-height: 1.1;
    }
  }

  &--txf {
    background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 55%, #0f766e 100%);
  }

  &--clickable {
    cursor: pointer;
    &:focus { outline: none; }
    &:hover .kpi-card__expand { opacity: 0.95; transform: scale(1.15); }
  }

  /* drill-down affordance (same idea as the uim-kpi--click tiles) */
  &__expand {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 3;
    font-size: 0.72rem;
    opacity: 0;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
}

/* FAKE money strip: a deliberately small footnote row of amber mini-pills —
   nothing like the big real-money cards, so fake money can't be misread.
   White-on-amber-gradient, so no dark-skin overrides are needed (same as the
   gradient KPI cards above). */
.fake-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem;

  &__badge {
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    border: 1px dashed rgba(255, 255, 255, 0.75);
    background: #ff9f43;
    color: #fff;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    line-height: 1.6;
  }
}

.fake-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #ffb75d, #f0862b);
  color: #fff;
  font-size: 0.74rem;
  line-height: 1.2;
  box-shadow: 0 2px 6px rgba(240, 134, 43, 0.25);

  &__label { opacity: 0.92; font-weight: 600; }
  &__value { font-weight: 800; font-size: 0.8rem; }
  &__count { opacity: 0.85; font-size: 0.7rem; }

  &__delta {
    display: inline-flex;
    align-items: center;
    gap: 0.1rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
    font-size: 0.64rem;
    font-weight: 700;

    &.is-up { color: #d6ffe8; }
    &.is-down { color: #ffe0e0; }
  }

  &--click {
    cursor: pointer;
    &:hover { filter: brightness(1.06); }
    &:focus { outline: none; }
  }
}

@media (max-width: 576px) {
  .kpi-card {
    min-height: auto;
    padding: 1rem;
    &__value { font-size: 1.3rem; }
  }
}
</style>
